import { FC } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

export type FeatureGridsProps = SliceComponentProps<Content.FeatureGrid2Slice>;

// ── EyebrowImageTopGrid variation ───────────────────────────────────────────

const EyebrowImageTopGrid: FC<{
  slice: Content.FeatureGrid2SliceEyebrowImageTopGrid;
}> = ({ slice }) => {
  const { eyebrow, section_heading, items, background_pattern } = slice.primary;

  return (
    <section
      data-slice-type="feature_grid_2"
      data-slice-variation="eyebrowImageTopGrid"
      className="relative overflow-hidden text-white"
    >
      {isFilled.image(background_pattern) && (
        <PrismicNextImage
          field={background_pattern}
          fill
          className="pointer-events-none absolute inset-0 object-cover opacity-50"
          fallbackAlt=""
        />
      )}

      <div className="relative z-10 mx-auto flex max-w-[1100px] flex-col items-center gap-8 px-6 py-16 md:px-10 md:py-20">
        {eyebrow && (
          <div className="inline-flex rounded-full border border-[#00f557] px-5 py-2">
            <span className="text-[14px] font-semibold uppercase leading-[1.2] tracking-wide text-white">
              {eyebrow}
            </span>
          </div>
        )}

        {isFilled.richText(section_heading) && (
          <PrismicRichText
            field={section_heading}
            components={{
              heading1: ({ children }) => (
                <h2 className="max-w-[920px] text-center text-[32px] font-bold leading-[1.15] md:text-[44px]">
                  {children}
                </h2>
              ),
              heading2: ({ children }) => (
                <h2 className="max-w-[920px] text-center text-[32px] font-bold leading-[1.15] md:text-[44px]">
                  {children}
                </h2>
              ),
              strong: ({ children }) => (
                <strong className="font-bold text-[#00f557]">{children}</strong>
              ),
            }}
          />
        )}

        <div className="mt-4 grid w-full grid-cols-1 gap-6 md:grid-cols-2">
          {items?.map((item, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 overflow-hidden rounded-2xl bg-[#eff1f3] p-4 text-[#041118]"
            >
              {item.image?.url && (
                <div className="aspect-video w-full overflow-hidden rounded-xl">
                  <PrismicNextImage
                    field={item.image}
                    className="h-full w-full object-cover"
                    fallbackAlt=""
                  />
                </div>
              )}
              {item.title && (
                <h3 className="text-[18px] font-bold leading-[1.3] md:text-[20px]">
                  {item.title}
                </h3>
              )}
              {item.description && (
                <p className="text-[14px] leading-[1.5] text-[#041118]/80">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── ImageTopGridWhitePanel variation ───────────────────────────────────────

const ImageTopGridWhitePanel: FC<{
  slice: Content.FeatureGrid2SliceImageTopGridWhitePanel;
}> = ({ slice }) => {
  const { title, items } = slice.primary;

  return (
    <section
      data-slice-type="feature_grid_2"
      data-slice-variation="imageTopGridWhitePanel"
      className="bg-[#0F423A] py-12 md:py-16"
    >
      <div className="mx-3 overflow-hidden rounded-[24px] bg-white px-5 py-12 text-[#031a14] sm:mx-5 sm:px-6 md:mx-[30px] md:px-12 md:py-16">
        {isFilled.richText(title) && (
          <div
            className="mx-auto max-w-[1000px] text-center capitalize"
            style={{
              color: '#041118',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              lineHeight: '120%',
            }}
          >
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[50px]">{children}</h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="text-[28px] md:text-[44px]">{children}</h3>
                ),
                strong: ({ children }) => (
                  <strong style={{ color: '#03BF9E', fontWeight: 500 }}>
                    {children}
                  </strong>
                ),
              }}
            />
          </div>
        )}

        {items && items.length > 0 && (
          <div className="mx-auto mt-10 grid max-w-[1100px] grid-cols-1 gap-6 md:mt-12 md:grid-cols-2 md:gap-8">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex flex-col overflow-hidden rounded-2xl border border-[#031a14]/10 bg-[#f7f8f6]"
              >
                {isFilled.image(item.image) && (
                  <PrismicNextImage
                    field={item.image}
                    className="block h-[200px] w-full object-cover md:h-[220px]"
                    fallbackAlt=""
                  />
                )}
                <div className="flex flex-col gap-3 px-5 py-5 md:px-6 md:py-6">
                  {item.title && (
                    <h4 className="text-[16px] font-bold leading-[1.3] text-[#031a14] md:text-[18px]">
                      {item.title}
                    </h4>
                  )}
                  {isFilled.richText(item.description) && (
                    <div className="text-[14px] leading-[1.55] text-[#031a14]/75 md:text-[15px]">
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="m-0">{children}</p>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold text-[#031a14]">
                              {children}
                            </strong>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

const FeatureGrids: FC<FeatureGridsProps> = ({ slice }) => {
  if (slice.variation === 'eyebrowImageTopGrid') {
    return <EyebrowImageTopGrid slice={slice} />;
  }
  if (slice.variation === 'imageTopGridWhitePanel') {
    return <ImageTopGridWhitePanel slice={slice} />;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-[50px] px-4 md:px-8 lg:px-[100px]"
    >
      <div className="max-w-[1240px] mx-auto flex flex-col items-center gap-6">
        {slice.primary.section_heading && (
          <h2 className="text-[28px] md:text-[42px] font-medium leading-[1.2] text-center text-[#041118] capitalize">
            {slice.primary.section_heading}
          </h2>
        )}

        <div className="flex flex-wrap justify-center gap-6 md:gap-10 w-full">
          {slice.primary.items.map((item, index) => (
            <div
              key={index}
              className="flex flex-col gap-6 p-6 border-2 border-[#EEEEEE] rounded-2xl w-full md:w-[calc(50%-20px)] max-w-[600px]"
            >
              {item.image?.url && (
                <div className="w-full h-[200px] md:h-[273px] rounded-2xl overflow-hidden">
                  <PrismicNextImage
                    field={item.image}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="flex flex-col gap-6">
                {item.title && (
                  <h3 className="text-xl md:text-[26px] font-semibold leading-[1.4] text-center text-[#041118] uppercase">
                    {item.title}
                  </h3>
                )}

                {item.description && (
                  <p className="text-base md:text-lg leading-[1.4] text-center text-[#041118]">
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureGrids;
